// src/redux/slice/creditCardSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { encryptData } from "../../utils/localEncruption";
import { decryptData, decryptAPIData } from "../../utils/localEncruption";

// Function to fetch and decrypt cards data from API
const fetchCardsFromAPI = async () => {
  const response = await fetch(`${process.env.REACT_APP_CC_URL}?view=table`);
  const encryptedData = await response.text();
  try {
    const decryptedData = await decryptAPIData(
      encryptedData,
      `${process.env.REACT_APP_CREDIT_CARD_SALT}`,
    );
    return decryptedData;
  } catch (error) {
    console.error("Decryption failed for credit card data:", error);
  }

  return response.json();
};

// Helper function to get data from local storage
export const getCardsFromLocalStorage = () => {
  const storedData = localStorage.getItem("cardsData");
  const salt = `${process.env.REACT_APP_SALT}`;
  if (!storedData) {
    return null;
  }

  try {
    const decryptedData = decryptData(storedData, salt);
    return decryptedData;
  } catch (error) {
    return null;
  }
};

// Helper function to save data to local storage
const saveCardsToLocalStorage = (data) => {
  const salt = `${process.env.REACT_APP_SALT}`;
  const encryptedData = encryptData(data, salt);
  localStorage.setItem("cardsData", encryptedData);
};

// Helper function to get the timestamp when data was last fetched
const getTimestampFromLocalStorage = (key) => {
  return localStorage.getItem(`${key}_timestamp`);
};

// Helper function to save the timestamp when data is fetched
const saveTimestampToLocalStorage = (key) => {
  const timestamp = new Date().getTime();
  localStorage.setItem(`${key}_timestamp`, timestamp);
};

// Define a threshold in milliseconds for data freshness (e.g., 6 hour)
const DATA_FRESHNESS_THRESHOLD = 6 * 60 * 60 * 1000; // 6 hour

// Thunk to fetch cards (with localStorage caching + encryption)
export const fetchCards = createAsyncThunk(
  "creditCard/fetchCards",
  async (_, thunkAPI) => {
    const cachedData = getCardsFromLocalStorage();
    const timestamp = getTimestampFromLocalStorage("cardsData");
    const currentTime = new Date().getTime();

    // Check if data in local storage is stale
    const isDataStale =
      !timestamp || currentTime - timestamp > DATA_FRESHNESS_THRESHOLD;

    if (!cachedData || isDataStale) {
      try {
        const data = await fetchCardsFromAPI();

        saveCardsToLocalStorage(data);
        saveTimestampToLocalStorage("cardsData");

        return data;
      } catch (error) {
        return thunkAPI.rejectWithValue(error.message);
      }
    }
    return cachedData;
  },
);

const BANK_KEYS = [
  "amex",
  "axis",
  "au",
  "bob",
  "federal",
  "hdfc",
  "hsbc",
  "icici",
  "idfc",
  "indusind",
  "kotak",
  "rbl",
  "sbi",
  "scb",
  "yes",
];

const initialState = {
  // Deferred: was getCardsFromLocalStorage() which performs synchronous AES
  // decryption, blocking the main thread during store creation. The fetchCards
  // thunk already recovers cached data from localStorage asynchronously.
  cards: [],
  loading: "idle",
  error: null,

  // ✨ UI state migrated from component:
  filters: {
    // Multi-select benefit chips (can combine)
    selectedBenefits: [], // array of: 'domestic', 'international', 'railways', 'golf', 'LowARPinterest', 'LowFXmarkup'

    // Checkbox filters (can combine with radio)
    LowFee: false,
    Midrange: false,
    Premium: false,
    UltraPremium: false,

    // Popular filter chip (active by default)
    popularFilter: true,
  },

  columnVisibility: {
    showDomesticColumn: true,
    showInternationalColumn: true,
    showRailwaysColumn: true,
    showGolfColumn: true,
    showGolfLinks: true,
    showGolfIntl: true,
    showGolfMinSpend: true,
  },
  selectedBanks: [],
  showAllBanks: true,
  sortConfig: { key: null, direction: "asc", userSelected: false },
  expandedRows: [], // could be an array of ids
  searchSelections: [],
};

const creditCardSlice = createSlice({
  name: "creditCard",
  initialState,
  reducers: {
    // Multi-select benefit chip toggle
    toggleBenefitChip(state, action) {
      const benefit = action.payload;
      // Reset userSelected flag when changing benefits to allow automatic sorting
      state.sortConfig.userSelected = false;
      state.filters.popularFilter = false;
      const idx = state.filters.selectedBenefits.indexOf(benefit);
      if (idx >= 0) {
        state.filters.selectedBenefits.splice(idx, 1);
      } else {
        state.filters.selectedBenefits.push(benefit);
      }
    },
    // Legacy single-select toggle (kept for backward compat, behaves as chip toggle)
    toggleBenefitFilter(state, action) {
      const benefit = action.payload;
      state.sortConfig.userSelected = false;
      state.filters.popularFilter = false;
      const idx = state.filters.selectedBenefits.indexOf(benefit);
      if (idx >= 0) {
        state.filters.selectedBenefits.splice(idx, 1);
      } else {
        state.filters.selectedBenefits.push(benefit);
      }
    },
    // Fee Range filters
    toggleFeeRangeFilter(state, action) {
      state.sortConfig.userSelected = true;
      state.filters.popularFilter = false;
      const key = action.payload;
      state.filters[key] = !state.filters[key];
    },
    // Column visibility
    toggleColumn(state, action) {
      const key = action.payload;
      state.columnVisibility[key] = !state.columnVisibility[key];
    },
    // Banks
    setSelectedBanks(state, action) {
      // state.sortConfig.userSelected = true;
      state.selectedBanks = action.payload;
      if (action.payload.length > 0) {
        state.filters.popularFilter = false;
      }
    },
    toggleBank(state, action) {
      state.sortConfig.userSelected = true;
      state.filters.popularFilter = false;
      const key = action.payload;
      const idx = state.selectedBanks.indexOf(key);
      if (idx >= 0) state.selectedBanks.splice(idx, 1);
      else state.selectedBanks.push(key);
    },
    toggleAllBanks(state) {
      // Use static BANK_KEYS rather than dynamic cards
      state.selectedBanks = [...BANK_KEYS];
      state.filters.popularFilter = false;
    },
    toggleShowAllBanks(state) {
      state.showAllBanks = !state.showAllBanks;
    },
    // Sorting
    setSortConfig(state, action) {
      state.sortConfig = {
        key: action.payload.key,
        direction: action.payload.direction,
        userSelected:
          action.payload.userSelected !== undefined
            ? action.payload.userSelected
            : false,
      };
    },

    // Reset user selection for sorting
    resetSortUserSelection(state) {
      state.sortConfig.userSelected = false;
    },
    // Expanded rows
    toggleRowExpansion(state, action) {
      const id = action.payload;
      if (state.expandedRows.includes(id)) {
        state.expandedRows = state.expandedRows.filter((x) => x !== id);
      } else {
        state.expandedRows.push(id);
      }
    },
    // Reset everything
    clearAllFilters(state) {
      state.filters = {
        selectedBenefits: [],
        LowFee: false,
        Midrange: false,
        Premium: false,
        UltraPremium: false,
        popularFilter: false,
      };
      state.columnVisibility = {
        showDomesticColumn: true,
        showInternationalColumn: true,
        showRailwaysColumn: true,
        showGolfColumn: true,
        showGolfLinks: true,
        showGolfIntl: true,
        showGolfMinSpend: true,
      };
      state.selectedBanks = [];
      state.showAllBanks = false;
      state.sortConfig = { key: null, direction: "asc" };
      state.expandedRows = [];
    },

    // Reset user selection for sorting
    clearBenefitsFilters(state) {
      state.filters.selectedBenefits = [];
      // Reset sort user selection when clearing benefits
      state.sortConfig.userSelected = false;
    },

    clearFeesFilters(state) {
      state.filters = {
        selectedBenefits: state.filters.selectedBenefits,
        LowFee: false,
        Midrange: false,
        Premium: false,
        UltraPremium: false,
      };
    },

    selectAllFeesFilters(state) {
      state.filters = {
        selectedBenefits: state.filters.selectedBenefits,
        LowFee: true,
        Midrange: true,
        Premium: true,
        UltraPremium: true,
        popularFilter: false,
      };
    },

    clearBanksFilters(state) {
      state.selectedBanks = [];
    },
    // 📌 set the array of selected search options
    setSearchSelections(state, action) {
      state.searchSelections = action.payload;
      if (action.payload.length > 0) {
        state.filters.popularFilter = false;
      }
    },
    // 📌 clear search
    clearSearchSelections(state) {
      state.searchSelections = [];
    },
    // Toggle Popular filter and clear others
    togglePopularFilter(state) {
      state.filters.popularFilter = !state.filters.popularFilter;
      if (state.filters.popularFilter) {
        state.filters.selectedBenefits = [];
        state.filters.LowFee = false;
        state.filters.Midrange = false;
        state.filters.Premium = false;
        state.filters.UltraPremium = false;
        state.selectedBanks = [];
        state.searchSelections = [];
        state.sortConfig = { key: "fee", direction: "asc", userSelected: false };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCards.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchCards.fulfilled, (state, action) => {
        state.loading = "idle";
        state.cards = action.payload;
      })
      .addCase(fetchCards.rejected, (state, action) => {
        state.loading = "idle";
        state.error = action.payload;
      });
  },
});

export const {
  toggleBenefitChip,
  toggleBenefitFilter,
  toggleColumn,
  toggleFeeRangeFilter,
  setSelectedBanks,
  toggleBank,
  toggleAllBanks,
  toggleShowAllBanks,
  setSortConfig,
  toggleRowExpansion,
  clearAllFilters,
  setSearchSelections,
  clearSearchSelections,
  clearBenefitsFilters,
  clearFeesFilters,
  clearBanksFilters,
  selectAllFeesFilters,
  togglePopularFilter,
} = creditCardSlice.actions;

export default creditCardSlice.reducer;
